/* eslint react-hooks/exhaustive-deps: "warn" */

import { useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { Button } from 'ui/atoms/Button/Button';
import { Eyebrow } from 'ui/atoms/Eyebrow/Eyebrow';
import { Icon } from 'ui/atoms/Icon/Icon';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './DataFlowSection.module.scss';

import type { DataFlowBadgeTone, DataFlowLink, DataFlowSectionProps } from './DataFlowSection.types';

const BADGE_TONES: Record<DataFlowBadgeTone, { color: string; background: string }> = {
  blue: { color: '#2b3cff', background: '#e9edff' },
  purple: { color: '#6a3cff', background: '#f0eaff' },
  teal: { color: '#0a7c8f', background: '#e2f4f7' },
  green: { color: '#0a8f52', background: '#e4f6ec' },
};

// Flow dots align to the title line (::after top ~37px + half of the 9px dot),
// not the row center — keep this in sync with `.flowRow::after` top in the SCSS.
const FLOW_DOT_OFFSET = 41;

const ChannelCta = ({ link }: { link: DataFlowLink }) => (
  <Button
    type="transparent"
    size="lg"
    href={link.href}
    className={styles.channelCta}
    meta={link.meta}
    suffix={!link.meta ? <Icon type="arrow-14" /> : undefined}
  >
    {link.label}
  </Button>
);

export const DataFlowSection = ({
  title,
  extra,
  sourceTitle,
  sourceText,
  sourceStat,
  sourceLink,
  channels,
  variant = 'flow',
  className,
}: DataFlowSectionProps) => {
  const isPipeline = variant === 'pipeline';
  const channelsRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<(HTMLDivElement | null)[]>([]);
  const dotOffsetsRef = useRef<number[]>([]);

  const [trackBounds, setTrackBounds] = useState({ top: 0, height: 0 });
  const [progress, setProgress] = useState(0);
  const [activeIndex, setActiveIndex] = useState(-1);

  useEffect(() => {
    let frame: number | null = null;

    const measure = () => {
      const offsets = rowRefs.current.map((row) =>
        row ? row.offsetTop + (isPipeline ? row.offsetHeight / 2 : FLOW_DOT_OFFSET) : 0,
      );

      dotOffsetsRef.current = offsets;
      setTrackBounds({ top: offsets[0] ?? 0, height: (offsets[offsets.length - 1] ?? 0) - (offsets[0] ?? 0) });
    };

    const update = () => {
      frame = null;

      if (!channelsRef.current || dotOffsetsRef.current.length === 0) {
        return;
      }

      const offsets = dotOffsetsRef.current;
      const first = offsets[0];
      const last = offsets[offsets.length - 1];
      const span = last - first || 1;

      const rect = channelsRef.current.getBoundingClientRect();
      const viewportCenter = window.innerHeight / 2;
      const currentY = viewportCenter - rect.top;

      setProgress(Math.max(0, Math.min(1, (currentY - first) / span)));
      setActiveIndex(offsets.reduce((acc, offset, index) => (currentY >= offset ? index : acc), -1));
    };

    const handleScroll = () => {
      if (frame === null) {
        frame = requestAnimationFrame(update);
      }
    };

    measure();
    update();

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', measure);

    const resizeObserver = new ResizeObserver(() => {
      measure();
      update();
    });

    if (channelsRef.current) {
      resizeObserver.observe(channelsRef.current);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', measure);
      resizeObserver.disconnect();

      if (frame !== null) {
        cancelAnimationFrame(frame);
      }
    };
  }, [channels, isPipeline]);

  return (
    <Section
      align="left"
      title={isPipeline ? undefined : title}
      extra={isPipeline ? undefined : extra}
      className={className}
    >
      <div className={cn(styles.flow, isPipeline && styles.flowPipeline)}>
        <div className={cn(styles.flowSource, isPipeline && styles.flowSourcePlain)}>
          {isPipeline && extra && <div className={styles.flowSourceExtra}>{extra}</div>}
          <TitleBlock
            align="left"
            theme={isPipeline ? 'light' : 'dark'}
            title={sourceTitle}
            description={sourceText}
            className={styles.flowSourceTitleBlock}
          />

          {sourceStat && (
            <div className={styles.flowSourceStat}>
              <span className={styles.flowSourceStatValue}>{sourceStat.value}</span>
              <span className={styles.flowSourceStatLabel}>{sourceStat.label}</span>
            </div>
          )}

          {sourceLink && (
            <a href={sourceLink.href} className={styles.flowSourceLink}>
              {sourceLink.label}
              <span className={styles.flowSourceLinkIconWrap}>
                <Icon type="arrow-14" className={styles.flowSourceLinkIcon} />
              </span>
            </a>
          )}
        </div>

        <div ref={channelsRef} className={styles.flowChannels}>
          <div
            className={cn(styles.flowTrack, isPipeline && styles.flowTrackPipeline)}
            aria-hidden
            style={{ top: trackBounds.top, height: trackBounds.height }}
          >
            <div className={styles.flowTrackProgress} style={{ height: `${progress * 100}%` }} />
          </div>

          {channels.map((channel, index) => {
            const links = channel.links ?? (channel.link ? [channel.link] : []);
            const tone = channel.badge?.tone ? BADGE_TONES[channel.badge.tone] : null;

            const badge = channel.badge && (
              <Eyebrow
                label={channel.badge.label}
                dot={false}
                pill={!tone}
                color={tone?.color ?? '#fff'}
                background={tone?.background ?? '#335cf4'}
                className={cn(
                  styles.badge,
                  isPipeline && styles.badgeBlock,
                  tone && styles.badgeTone,
                  channel.badge.mono && styles.badgeMono,
                )}
              />
            );

            return (
              <div
                key={channel.title}
                ref={(el) => {
                  rowRefs.current[index] = el;
                }}
                className={cn(
                  styles.flowRow,
                  isPipeline && styles.flowRowCard,
                  !isPipeline && links.length === 0 && channel.sub && styles.flowRowWide,
                  index <= activeIndex && styles.flowRowActive,
                )}
              >
                {isPipeline && badge}

                <h4 className={styles.flowRowTitle}>
                  {channel.title}
                  {!isPipeline && badge}
                </h4>
                <div className={styles.flowRowText}>
                  {channel.description && <p className={styles.flowRowDescription}>{channel.description}</p>}

                  {isPipeline && links.length > 0 && (
                    <span className={styles.actions}>
                      {links.map((link) => (
                        <ChannelCta key={link.href + link.label} link={link} />
                      ))}
                    </span>
                  )}

                  {channel.sub && (
                    <div className={styles.flowRowSub}>
                      {channel.sub.map((item) => (
                        <div key={item.title} className={styles.flowRowSubItem}>
                          <b>{item.title}</b>
                          <span>
                            {item.text} {item.code && <code>{item.code}</code>}
                          </span>
                          <span className={styles.actions}>
                            <ChannelCta link={item.link} />
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {!isPipeline && links.length > 0 && (
                  <span className={cn(styles.actions, styles.actionsChannel)}>
                    {links.map((link) => (
                      <ChannelCta key={link.href + link.label} link={link} />
                    ))}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </Section>
  );
};
